import { Button, Col, ConfigProvider, Form, Input, Modal, Row, Tooltip } from "antd";
import { FileBrowsingTable } from "../file-browser/file-browser-table";
import { useEffect, useState } from "react";
import { PathArrayComponent } from "../path-array/path-array";
import "./folder-modal.css";
import { ContainerData, RequestDataQuery } from "../../../api/data-types";
import { StorageAPIClient } from "../../../api/storage-api-client";
import { PagingStack } from "../../browse-page/util/data-types/data-types-browse-page";
import { pathToArray } from "../../browse-page/util/browse-page-util";
import { emptyRequestDataProperties as emptyData } from "../../../api/data-types";
import Search from "antd/es/input/Search";
import { text } from "stream/consumers";

interface FolderModalProps {
  theme: string;
  isOpen: boolean;
  containerName: string;
  rootFolderPath: string;
  sendPathToParent: (path: string, updatePath: boolean) => void;
  showInput: boolean;
  numberOfDisabledInPath: number;
}

interface PathArrayProps {
  pathElements: string[];
  path?: string;
}

interface FormData {
  folder: string;
}

export const FolderModal = ({
  theme,
  isOpen,
  containerName,
  rootFolderPath,
  sendPathToParent,
  showInput,
  numberOfDisabledInPath,
}: FolderModalProps) => {
  const limit = 10;

  const [form] = Form.useForm<FormData>();

  const [formFolderPattern] = Form.useForm<FormData>();

  const [requestDataQuerry, setRequestDataQuerry] = useState<RequestDataQuery>(
    new RequestDataQuery(
      emptyData.EMPTY_FOLDERS,
      emptyData.EMPTY_FILES,
      emptyData.EMPTY_PATH,
      containerName,
      rootFolderPath,
      emptyData.EMPTY_CURRENT_TOKEN,
      emptyData.EMPTY_NEXT_TOKEN,
      emptyData.EMPTY_PREVIOUS_TOKEN,
      limit,
      false
    )
  );

  const [pathArray, setPathArray] = useState<PathArrayProps>({
    pathElements: [],
    path: "",
  });

  const [isVisible, setIsVisible] = useState<boolean>(isOpen);

  const [currentPagingStack, setCurrentPagingStack] = useState<PagingStack>(
    new PagingStack([])
  );

  const [formData, setFormData] = useState<FormData>({
    folder: "",
  });

  const [loading, setLoading] = useState<boolean>(false);

  const updateComponentsDataQuerry = async (
    containerName: string,
    offset: number,
    previousOffset: number,
    parentFolder?: string,
    folderPattern?: string
  ): Promise<RequestDataQuery> => {
    const newDataQuerry = StorageAPIClient.getFoldersFromContainer(
      containerName,
      offset,
      previousOffset,
      limit,
      parentFolder,
      folderPattern
    );
    return newDataQuerry;
  };

  const navigateUsingContinuationToken = async (token: string) => {
    if (token === requestDataQuerry.nextContinuationToken) {
      await updateComponentsDataQuerry(
        requestDataQuerry.containerName,
        Number(token),
        Number(requestDataQuerry.currentContinuationToken),
        requestDataQuerry.parentFolder
      ).then((dataQuerry) => {
        currentPagingStack.previousTokens.push(
          requestDataQuerry.previousContinuationToken
        );
        setRequestDataQuerry(dataQuerry);
        const tempPath =
          dataQuerry.containerName + "/" + dataQuerry.parentFolder;
        setPathArray({ pathElements: pathToArray(tempPath), path: tempPath });
      });
    }
    if (token === requestDataQuerry.previousContinuationToken) {
      await updateComponentsDataQuerry(
        requestDataQuerry.containerName,
        Number(token),
        Number(
          currentPagingStack.previousTokens[
            currentPagingStack.previousTokens.length - 1
          ]
        ),
        requestDataQuerry.parentFolder
      ).then((dataQuerry) => {
        currentPagingStack.previousTokens.pop();
        setRequestDataQuerry(dataQuerry);
        const tempPath =
          dataQuerry.containerName + "/" + dataQuerry.parentFolder;
        setPathArray({ pathElements: pathToArray(tempPath), path: tempPath });
      });
    }
  };

  const onValuesChange = () => {
    setFormData({
      folder: form.getFieldValue("folder"),
    });
  };

  const handleSearchClick = async () => {
    setLoading(true);
    await updateComponentsDataQuerry(
      requestDataQuerry.containerName,
      Number(emptyData.EMPTY_OFFSET),
      Number(emptyData.EMPTY_OFFSET),
      requestDataQuerry.parentFolder,
      formFolderPattern.getFieldValue("folderPattern")
    ).then((dataQuerry) => {
      setRequestDataQuerry(dataQuerry);
      const tempPath = dataQuerry.containerName + "/" + dataQuerry.parentFolder;
      setPathArray({ pathElements: pathToArray(tempPath), path: tempPath });
      formFolderPattern.setFieldValue("folderPattern", "");
    });
    setLoading(false);
  };

  const handlePathArrayClick = async (
    pathElements: string[],
    index: number
  ) => {
    var dataQuerry: RequestDataQuery;
    if (index === 0) {
      dataQuerry = await updateComponentsDataQuerry(
        pathElements[index],
        Number(emptyData.EMPTY_OFFSET),
        Number(emptyData.EMPTY_OFFSET)
      );
    } else {
      const folder = pathElements.slice(1, index + 1).join("/");
      dataQuerry = await updateComponentsDataQuerry(
        pathElements[0],
        Number(emptyData.EMPTY_OFFSET),
        Number(emptyData.EMPTY_OFFSET),
        folder
      );
    }
    dataQuerry.files = emptyData.EMPTY_FILES;
    setRequestDataQuerry(dataQuerry);
    const tempPath = dataQuerry.containerName + "/" + dataQuerry.parentFolder;
    setPathArray({ pathElements: pathToArray(tempPath), path: tempPath });
    setCurrentPagingStack(new PagingStack([]));
  };

  const handleFolderClick = async (folderName: string) => {
    updateComponentsDataQuerry(
      containerName,
      Number(emptyData.EMPTY_OFFSET),
      Number(emptyData.EMPTY_OFFSET),
      folderName
    ).then((dataQuerry) => {
      setRequestDataQuerry(dataQuerry);
      const tempPath = dataQuerry.containerName + "/" + dataQuerry.parentFolder;
      setPathArray({ pathElements: pathToArray(tempPath), path: tempPath });
      setCurrentPagingStack(new PagingStack([]));
    });
  };

  const handleSelectFolder = (folderName: string) => {
    let tmpPath = pathArray.pathElements[0];
    if (folderName !== "") {
      tmpPath += "/" + folderName;
    }
    setPathArray({ pathElements: pathToArray(tmpPath), path: tmpPath });
    setIsVisible(false);
    tmpPath = pathToArray(tmpPath).slice(1).join("/");
    sendPathToParent(tmpPath, true);
  };

  const handleOkClick = () => {
    setIsVisible(false);
    let tmpPath = pathArray.pathElements.slice(1).join("/");
    if (formData.folder !== "") {
      if (tmpPath !== "") {
        tmpPath += "/";
      }
      tmpPath += formData.folder;
    }
    sendPathToParent(tmpPath, true);
  };

  useEffect(() => {
    setIsVisible(isOpen);
    if (!isOpen) {
      setPathArray({
        pathElements: [],
        path: "",
      });
      setRequestDataQuerry(
        new RequestDataQuery(
          emptyData.EMPTY_FOLDERS,
          emptyData.EMPTY_FILES,
          emptyData.EMPTY_PATH,
          containerName,
          rootFolderPath,
          emptyData.EMPTY_CURRENT_TOKEN,
          emptyData.EMPTY_NEXT_TOKEN,
          emptyData.EMPTY_PREVIOUS_TOKEN,
          limit,
          false
        )
      );
    } else {
      updateComponentsDataQuerry(
        containerName,
        Number(emptyData.EMPTY_OFFSET),
        Number(emptyData.EMPTY_OFFSET),
        rootFolderPath
      ).then((dataQuerry) => {
        setRequestDataQuerry(dataQuerry);
        const tempPath =
          dataQuerry.containerName + "/" + dataQuerry.parentFolder;
        setPathArray({ pathElements: pathToArray(tempPath), path: tempPath });
      });
    }
    setFormData({
      folder: "",
    });
    form.setFieldValue("folder", "");
  }, [isOpen, containerName]);

  const textColor = theme === "light" ? "#000000" : "#ffffff";

  return (
    <div data-theme={theme}>
      <Modal
        open={isVisible}
        closable={false}
        width={"100%"}
        footer={[
          <Button
            key="cancel "
            onClick={() => {
              setIsVisible(false);
              sendPathToParent("", false);
            }}
          >
            Cancel
          </Button>,
          <Button type="primary" onClick={handleOkClick}>
            OK
          </Button>,
        ]}
        className="folderModal"
      >
        <Row justify="start">
          <Col span={13}>
            <PathArrayComponent
              theme={theme}
              pathElements={pathArray.pathElements}
              onPathArrayClick={handlePathArrayClick}
              numberOfDisabled={numberOfDisabledInPath}
            />
          </Col>
          <Col
            span={5}
            className="folderInputText"
            style={{ color: textColor }}
          >
            Filter folders:
          </Col>
          <Col span={6}>
            <Form form={formFolderPattern}>
              <Form.Item name="folderPattern">
                <Search
                  className="searchInput"
                  placeholder="..."
                  onSearch={handleSearchClick}
                  style={{ width: "70%" }}
                  loading={loading}
                />
              </Form.Item>
            </Form>
          </Col>
        </Row>
        <Row>
          <FileBrowsingTable
            selectedSearchType="none"
            theme={theme}
            filterOn={false}
            dataQuerry={requestDataQuerry}
            onFolderClick={handleFolderClick}
            selectedRowKeys={[]}
            setSelectedRowKeys={() => {}}
            showFolderColumn={false}
            showSelectFolderAction={true}
            onSelectFolderClick={handleSelectFolder}
            continuationTokenNavigation={navigateUsingContinuationToken}
            previousTokens={currentPagingStack.previousTokens}
            ySizeVH={showInput ? "40vh" : "57vh"}
            sendFilterAndSorting={() => {}}
            filterFieldsFromParent={[]}
            sizesWithUnitFromParent={[]}
          />
        </Row>
        <Row className="formModal">
          {showInput && (
            <Form form={form}>
              <Row justify="start">
                <Col
                  span={12}
                  className="folderInputText"
                  style={{ color: textColor }}
                >
                  Create new sub folder :
                </Col>
                <Col span={12}>
                  <Form.Item name="folder">
                    <Input
                      style={{ color: textColor }}
                      className="folderInput"
                      placeholder="SubFolder"
                      onChange={onValuesChange}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {/* <Row justify="start">
                <Col
                  span={12}
                  className="folderInputText"
                  style={{ color: textColor }}
                >
                  Confirm new folder name:
                </Col>
                <Col span={12}>
                  <Form.Item name="confirmFolder">
                    <Input
                      style={{ color: textColor }}
                      className="folderInput"
                      placeholder="/folder/"
                      onChange={onValuesChange}
                      onCopy={(e) => {
                        e.preventDefault();
                        return false;
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        return false;
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row justify="start">
                <span className="warningText">
                  *Copying and pasting is disabled
                </span>
              </Row> */}
            </Form>
          )}
        </Row>
      </Modal>
    </div>
  );
};
